import {filter, map, size} from 'lodash';
import {orderBy} from 'natural-orderby';

import Loading from 'components/Loading';
import InlinePage from 'components/Page/Inline';
import {baseAccIdColumn} from 'components/Table/Cell/AccId';
import BooleanCell from 'components/Table/Cell/Boolean';
import BooleanSelectFilter from 'components/Table/Filter/Boolean';
import TextFilter from 'components/Table/Filter/Text';
import SimpleTable from 'components/Table/Simple';
import {flexColumnStyle} from 'components/Table/columnHelpers';
import KyError from 'components/errors/KyError';
import ResourceLink from 'components/links/Resource';
import FullFluidWidthTemplate from 'components/templates/FullFluidWidth';
import useAppContext from 'conf/AppContext';
import {useGet} from 'helpers/KyHooks';

export default function PgxGenesPage({}) {
  const appContext = useAppContext();
  const {response, error} = useGet('site/gene', {
    cacheResponse: !appContext.isPreview,
    parseJson: true,
  });
  if (error) {
    appContext.toastError(<KyError kyError={error} />);
  }

  let body = <Loading />;
  if (response) {
    const {data} = response;
    const rows = filter(data, (r) => r.guidelineSources?.length > 0 || r.labelSources?.length > 0);
    const rowsOther = filter(data, (r) => size(r.guidelineSources) === 0 && size(r.labelSources) === 0);
    body = (
      <div>
        <div className="container">
          <InlinePage id="pgxGenesHeader" showTitle={false} />
        </div>

        <div>
          <PgxGeneTable rows={rows} />
        </div>

        <div className="container">
          <h4>Other Genes with Top-Level ClinPGx Summary Annotations</h4>
          <p>These genes have ClinPGx Summary Annotations with a Level of Evidence 1B, 2A or 2B that do not have actionable drug label annotation or actionable PGx guideline annotations.</p>
          <ul className="list-inline" style={{gap: '2em'}}>
            {map(orderBy(rowsOther, [(r) => r.gene.symbol]), (r) => <li key={r.gene.id} className="list-inline-item"><ResourceLink resource={r.gene} /></li>)}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <FullFluidWidthTemplate title="Actionable Pharmacogenes">
      {body}
    </FullFluidWidthTemplate>
  );
}

const PgxGeneTable = ({rows, title}) => {
  const columns = [
    {
      id: 'gene',
      Header: 'Gene',
      accessor: 'gene',
      ...baseAccIdColumn,
      ...flexColumnStyle({width: 200}),
    },
    {
      id: 'summaryLevel',
      Header: 'Top Summary Level',
      accessor: 'summaryLevel',
      Filter: TextFilter,
      sortable: true,
      ...flexColumnStyle({width: 150}),
    },
    {
      id: 'hasFdaLabel',
      Header: 'FDA Label',
      accessor: (row) => row.labelSources.includes('FDA'),
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 140}),
    },
    {
      id: 'hasEmaLabel',
      Header: 'EMA Label',
      accessor: (row) => row.labelSources.includes('EMA'),
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 140}),
    },
    {
      id: 'hasHcscLabel',
      Header: 'HCSC Label',
      accessor: (row) => row.labelSources.includes('HCSC'),
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 140}),
    },
    {
      id: 'hasGuidelineCpic',
      Header: 'CPIC Guideline',
      accessor: (row) => row.guidelineSources.includes('CPIC'),
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 140}),
    },
    {
      id: 'hasGuidelineDpwg',
      Header: 'DPWG Guideline',
      accessor: (row) => row.guidelineSources.includes('DPWG'),
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 140}),
    },
    {
      id: 'hasGuidelineOther',
      Header: 'Other Guideline',
      accessor: (row) => size(filter(row.guidelineSources, (s) => s !== 'CPIC' && s !== 'DPWG')),
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 140}),
    },
    {
      id: 'hasFdaPgxAssoc',
      Header: 'FDA PGx Table',
      accessor: 'hasFdaPgxAssoc',
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 120}),
    },
    {
      id: 'hasRec',
      Header: 'GSI/PharmDOG',
      accessor: 'hasRec',
      Cell: BooleanCell,
      Filter: BooleanSelectFilter,
      sortable: true,
      ...flexColumnStyle({width: 120}),
    },
  ];
  return (
    <SimpleTable
      title={title}
      data={rows}
      disableExport={false}
      columns={columns}
      initialState={{sortBy: [{id: 'gene', desc: false}]}}
    />
  );
};
